import { useNavigate } from 'react-router-dom';
import { Badge, Button, Text } from 'components';
import { Payment, BTPaymentGridData, PaymentList } from 'types/payment';
import { useEffect, useState } from 'react';
import axios from 'axios';
import { getCookie } from 'common/Cookie';
import { useTranslation } from 'react-i18next';

// 가격 포맷팅 함수
const formatPrice = (amount: number | string, currency?: string) => {
  const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
  const formattedNumber = numAmount.toLocaleString();
  
  if (currency === 'KRW') {
    return `₩${formattedNumber}`;
  } else {
    // KRW가 아니면 무조건 USD로 처리
    return `$${formattedNumber}`;
  }
};

interface PaymentInfoColumnProps {
  label: string;
  text: any;
  className?: string;
}

interface PaymentInfoProps {
  paymentInfo: BTPaymentGridData;
  index: number;
}

const PaymentInfoCol = ({ label, text, className = "" }: PaymentInfoColumnProps) => {
  return (
    <div className={`flex flex-col gap-1 ${className}`}>
      <Text size="small" weight="regular" color="secondary">
        {label}
      </Text>
      <Text size="body2" weight="regular" color="primary">
        {text}
      </Text>
    </div>
  );
};

// 결제 정보 항목을 표시하는 컴포넌트
const PaymentInfoElement = ({ paymentInfo, index }: PaymentInfoProps) => {
  return (
    <li className="w-full">
      <div className="mb-4 flex w-full flex-row items-center gap-6">
        <div className="flex flex-1 flex-row gap-6">
          {/* orderId와 totalAmount를 표시 - 원래 레이아웃 유지하되 공간 배분 개선 */}
          <PaymentInfoCol 
            label="Order ID" 
            text={paymentInfo.orderId} 
            className="min-w-0 flex-[2]"
          />
          <PaymentInfoCol 
            label="price" 
            text={formatPrice(paymentInfo.totalAmount, paymentInfo.currency)} 
            className="min-w-0 flex-1"
          />
          <PaymentInfoCol 
            label="paymentDate" 
            text={paymentInfo.paidDate} 
            className="min-w-0 flex-[1.5]"
          />
          <PaymentInfoCol 
            label="paymentStatus" 
            text={paymentInfo.status} 
            className="min-w-0 flex-1"
          />
          <PaymentInfoCol 
            label="cancelStatus" 
            text={paymentInfo.cancelStatus || 'none'} 
            className="min-w-0 flex-1"
          />
        </div>
      </div>
      <hr />
    </li>
  );
};

const PaymentInfoSection = () => {
  const nav = useNavigate();
  const [paymentData, setPaymentData] = useState<BTPaymentGridData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const pageNum = 1;
  const pageSize = 10;
  const { t } = useTranslation();

  // API 호출 함수
  const fetchPaymentData = async () => {
    try {
      setLoading(true);
      const token = getCookie('accessToken');
      const res = await axios.get(`/api/confirm/list/${pageNum}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          pageSize,
        },
      });
      const { paymentList = [] } = res.data;
      console.log("나다 sheep"+JSON.stringify(res.data))
      
      const selectData: BTPaymentGridData[] = paymentList.map((item: any) => ({
        orderId: item.orderId,
        totalAmount: item.amount,
        currency: item.currency, // currency 정보 추가
        paidDate: item.paidDate,
        status: item.status,
        cancelStatus: item.cancelStatus
      }));

      setPaymentData(selectData);
    } catch (error) {
      setError('Failed to load payment data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPaymentData();
  }, []);

  function onClickScheduleButton() {
    nav('/manage-contacts');
  }

  return (
    <section className="w-full rounded-2xl border border-border-primary bg-bg-layer3 p-4">
      <Text className="mb-4" size="body1" color="black" weight="medium">
        {t('myPage.bank.title')} {/* Bank Transfer Payment */}
      </Text>
      <ul className="mb-4 flex w-full flex-col gap-4 overflow-x-auto overflow-y-visible">
        {loading && <Text>Loading...</Text>}
        {error && <Text>{error}</Text>}
        {!loading && !error && paymentData.length > 0 ? (
          paymentData.map((paymentInfo, index) => (
            <PaymentInfoElement key={paymentInfo.orderId} index={index} paymentInfo={paymentInfo} />
          ))
        ) : (
          <Text>{t('myPage.bank.empty')}</Text>
        )}
      </ul>
      <Button 
        text={t('myPage.bank.button')}
        type="secondary" 
        onClick={onClickScheduleButton} 
      />
    </section>
  );
};

export default PaymentInfoSection;